import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  TablePagination,
  TextField,
  Box,
} from "@mui/material";
import {
  makeGETRequestOnUserService,
  makePUTRequestOnUserService,
} from "../../Api/api";
import toastr from "toastr";
import { User } from "./DownloadPermissions";

const DeactivateUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterName, setFilterName] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterLeadOwner, setFilterLeadOwner] = useState("");
  const [filterUserType, setFilterUserType] = useState("");

  useEffect(() => {
    setIsLoading(true);
    makeGETRequestOnUserService("/user/user_list/?user_type=all")
      .then((resp) => {
        if (resp.status === 200) {
          setUsers(resp.data);
        }
      })
      .catch(() => {
        toastr.error("Failed to fetch user list.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterName, filterMobile, filterEmail, filterLeadOwner, filterUserType]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return (
        (filterName ? fullName.includes(filterName.toLowerCase()) : true) &&
        (filterMobile
          ? user.mobileNumber.toLowerCase().includes(filterMobile.toLowerCase())
          : true) &&
        (filterEmail
          ? user.emailId.toLowerCase().includes(filterEmail.toLowerCase())
          : true) &&
        (filterLeadOwner
          ? (user.leadowner || "")
            .toLowerCase()
            .includes(filterLeadOwner.toLowerCase())
          : true) &&
        (filterUserType
          ? (user.user_type || "")
            .toLowerCase()
            .includes(filterUserType.toLowerCase())
          : true)
      );
    });
  }, [users, filterName, filterMobile, filterEmail, filterLeadOwner, filterUserType]);

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: User
  ) => {
    const disabled = event.target.checked;
    const newIsActive = !disabled;
    const userType = user.user_type?.toLowerCase();
    const url =
      userType === "partner" || userType === "client"
        ? `/user/deactivate/${user.uuid}/?is_active=${newIsActive}&user_type=${userType}`
        : `/user/deactivate/${user.uuid}/?is_active=${newIsActive}`;

    setUsers((prev) =>
      prev.map((u) =>
        u.uuid === user.uuid ? { ...u, isActive: newIsActive } : u
      )
    );

    makePUTRequestOnUserService(url)
      .then((resp) => {
        if (resp.status === 200) {
          toastr.success(
            newIsActive
              ? "User re-activated successfully"
              : "User deactivated successfully"
          );
        } else {
          throw new Error("Non-200 status");
        }
      })
      .catch(() => {
        toastr.error(
          newIsActive
            ? "Failed to re-activate user"
            : "Failed to deactivate user"
        );
        setUsers((prev) =>
          prev.map((u) =>
            u.uuid === user.uuid ? { ...u, isActive: !newIsActive } : u
          )
        );
      });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) =>
      a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1
    );
  }, [filteredUsers]);

  const paginatedUsers =
    rowsPerPage > 0
      ? sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedUsers;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "90%",
        mx: "auto",
        zoom: 0.8,
        borderRadius: 3,
        border: "1px solid grey",
        my: 2,
      }}
    >
      <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Filter by Name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          size="small"
        />
        <TextField
          label="Filter by User Type"
          value={filterUserType}
          onChange={(e) => setFilterUserType(e.target.value)}
          size="small"
        />
        <TextField
          label="Filter by Mobile Number"
          value={filterMobile}
          onChange={(e) => setFilterMobile(e.target.value)}
          size="small"
        />
        <TextField
          label="Filter by Email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
        />
        <TextField
          label="Filter by Lead Owner"
          value={filterLeadOwner}
          onChange={(e) => setFilterLeadOwner(e.target.value)}
          size="small"
        />
      </Box>
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#e9e8e8" }}>
            <TableRow>
              <TableCell>Select (deactivate)</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Lead Owner</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.uuid}>
                <TableCell>
                  <Checkbox
                    checked={!user.isActive}
                    onChange={(e) => handleCheckboxChange(e, user)}
                  />
                </TableCell>
                <TableCell>{user.user_type?.toUpperCase() || "-"}</TableCell>
                <TableCell>{user.company_name || `${user.firstName} ${user.lastName}`}</TableCell>
                <TableCell>{user.mobileNumber}</TableCell>
                <TableCell>{user.emailId}</TableCell>
                <TableCell>{user.leadowner}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, { value: -1, label: "All" }]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage > 0 ? rowsPerPage : filteredUsers.length}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default DeactivateUsers;
